import React, { useRef, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import { getTranslation } from "../common";
import { useSelectedOrganization, useSnackbar, useTranslations } from "hooks";
import { CloudUploadRounded } from "@mui/icons-material";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import useSubscriptionFeatures from "hooks/useSubscriptionFeatures";
import { useNavigate } from "react-router-dom";
import CallMadeIcon from '@mui/icons-material/CallMade';
import FlowentiButton from "./flowenti-ui-kit/FlowentiButton";

interface DragDropProps {
  onFileChange: (files: File[]) => void;
  files: File[];
  maxFiles?: number;
}
const DropZone: React.FC<DragDropProps> = ({ onFileChange, files, maxFiles }) => {
  // const [files, setFiles] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const translations = useTranslations();
  const { bypassSubscription, storageDiskCanUse, refreshSubscriptionFeatures, storageUsage, checkLoading } = useSubscriptionFeatures();
  const organization = useSelectedOrganization()
  const navigate = useNavigate()
  const { sendSnack } = useSnackbar();
  const uploadFiles = (newFiles: File[]) => {
    const fileSize = newFiles.reduce((acc, file) => acc + file?.size, 0)
    const totalFiles = files.length + newFiles.length;

    if (maxFiles && totalFiles > maxFiles) {
      sendSnack({
        message: `puoi allegare al massimo ${maxFiles} file.`,
        type: "error",
      });
      return;
    }
    if (!bypassSubscription) {
      const usage = (storageUsage?.using || 0) + files.reduce((acc, file) => acc + file?.size, 0)
      if ((fileSize + usage) <= (storageUsage?.permit || 0)) {
        onFileChange([...files, ...newFiles]);
        refreshSubscriptionFeatures();
      } else {
        sendSnack({
          message: "usage exceeded",
          type: "error",
        });
      }
    } else {
      onFileChange([...files, ...newFiles]);
      refreshSubscriptionFeatures();
    }
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const newFiles = Array.from(event.dataTransfer.files);
    uploadFiles(newFiles)
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      uploadFiles(newFiles)
    }
  };

  useEffect(() => {
    return () => {
      // setFiles([]);
      onFileChange([]);
      refreshSubscriptionFeatures()
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDelete = (index: number) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    // setFiles(updatedFiles);
    onFileChange(updatedFiles);
  };

  return (<>
    {checkLoading ? <CircularProgress /> :
      <div>
        {!bypassSubscription && !storageDiskCanUse ?
          <Box display="flex" alignItems="center"><Alert sx={{ height: 40, alignItems: "center", overflowY: "hidden", mr: 2 }} severity="error">{getTranslation(translations, "storage.limit.reached")}
          </Alert>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(`/dashboard/organizations/${organization?.id}/billing`, { state: { organizationName: organization?.name } })}
            >
              {getTranslation(translations, "generic.upgrade")}
            </Button>
          </Box> : <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            style={{
              border: "2px dashed #ccc",
              borderRadius: "10px",
              padding: "20px",
              textAlign: "center",
            }}
          >
            <Typography sx={{ pb: 4 }} variant="body1">
              {getTranslation(translations, "dropzone.drag_drop_caption")}
            </Typography>
            <input
              type="file"
              multiple
              onChange={handleFileInputChange}
              style={{ display: "none" }}
              ref={fileInput}
            />
            <FlowentiButton
              id="upload-attachment-button"
              size="small"
              startIcon={<CloudUploadRounded />}
              onClick={() => {
                fileInput.current?.click();
              }}
              sx={{
                backgroundColor: "FFFFFF",
                border: "1px solid #DEE2E6",
                "&:hover": {
                  backgroundColor: "",
                },
              }}
            >
              {getTranslation(translations, "generic.select_file.caption")}
            </FlowentiButton>
          </div>}
        <List
          sx={{
            maxHeight: "140px",
            overflowX: "auto",
          }}
        >
          {files.map((file, index) => (
            <ListItem key={index}>
              <ListItemText>
                <AttachFileOutlinedIcon />
                <span>{file.name}</span>
              </ListItemText>
              <Tooltip title="Elimina">
                <IconButton
                  onClick={() => onDelete(index)}
                  sx={{
                    color: "rgba(84, 189, 193, 1)",
                    borderRadius: "6px",
                    backgroundColor: "rgba(84, 189, 193, 0.3)",
                    "&:hover": {
                      backgroundColor: "rgba(84, 189, 193, 0.4)",
                    },
                  }}
                >
                  <DeleteForeverRoundedIcon />
                </IconButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      </div>}
  </>
  );
};

export default DropZone;
